import { NavLink, useLocation, useParams } from "react-router-dom";
import "./form-header-navigation.scss";
import { LeftIcon, PlayIcon, SettingIcon, ShareIcon } from "@components/icons";
import { Button } from "@components/button";
import { FC, useCallback, useMemo, useState } from "react";
import { CustomTooltip } from "@components/tool-tip/";
import { Permission } from "@contexts/permissions.context";
import { AppAndFormAction, AppAndFormStatus, PermissionTypes } from "@archetypes/gql";
import { UPDATE_FORM_STATUS } from "@queries/index";
import { useMutation } from "@apollo/client/react";
import ShareFormDialog from "@dialogs/share-form-dialog";
import { FormSettingDialog } from "@dialogs/form-settings";
import { NetworkStatusBar } from "@components/network-status-bar";
import { useToastContext } from "@contexts/toast";
import { useLayoutSave } from "@contexts/layout-save.context";

interface FormHeaderNavigatonInterface {
  formName?: string;
  formDescription?: string;
  actions?: AppAndFormAction[];
}

export const FormHeaderNavigaton: FC<FormHeaderNavigatonInterface> = ({
  formName,
  formDescription,
  actions,
}) => {
  const { appId, formId, layoutId } = useParams();
  const { pathname } = useLocation();
  const { showToast } = useToastContext();
  const { saveFn, isSaving } = useLayoutSave();

  const [isShareModalOpen, setShareModalOpen] = useState<boolean>(false);
  const [isSettingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
  const baseRoute = useMemo(() => {
    return "/builder/apps/" + appId + "/forms/" + formId;
  }, [appId, formId]);
  const [updateFormStatus, { loading: loadingFormStatus }] = useMutation<{
    updateFormStatus: { currentStatus: AppAndFormStatus };
  }>(UPDATE_FORM_STATUS, {
    onError: () => {
      showToast({
        title: "Error",
        content: `Error updating form status`,
        variant: "danger",
      });
    },
    onCompleted: (data) => {
      const status = data.updateFormStatus.currentStatus;
      if (status === AppAndFormStatus.PUBLISHED) {
        showToast({
          title: "Success",
          content: `Form published successfully`,
          variant: "success",
        });
      } else if (status === AppAndFormStatus.UNPUBLISHED) {
        showToast({
          title: "Success",
          content: `Form unpublished successfully`,
          variant: "success",
        });
      }
    },
  });

  const triggerFormStatusUpdate = useCallback(
    (status: AppAndFormAction) => {
      updateFormStatus({
        variables: {
          input: {
            formId: formId,
            currentStatus: status + "ED",
          },
        },
      });
    },
    [updateFormStatus, formId],
  );

  const { isPublishAllowed, isUnPublishAllowed } = useMemo(() => {
    return {
      isPublishAllowed: actions?.includes(AppAndFormAction.PUBLISH),
      isUnPublishAllowed: actions?.includes(AppAndFormAction.UNPUBLISH),
    };
  }, [actions]);

  const backLink = useMemo(() => {
    if (layoutId) {
      return "/builder/apps/" + appId + "/forms/" + formId + "/layouts";
    }
    return "/builder/apps/" + appId + "/forms";
  }, [appId, formId, layoutId]);

  const { showFormControls, showLayoutControls } = useMemo(() => {
    return {
      showFormControls: pathname.includes("/design") || pathname.includes("/preview"),
      showLayoutControls: pathname.includes("/layouts/" + layoutId),
    };
  }, [layoutId, pathname]);

  return (
    <>
      <nav className="top-menu-container" aria-label="Form Header Navigation">
        <div className="logo-area d-flex justify-center">
          <NavLink to={backLink}>
            <LeftIcon />
          </NavLink>
        </div>
        <div className="title-description-area">
          <h3>{formName}</h3>
          <p>{formDescription}</p>
        </div>
        <ul className="top-menu">
          <li>
            <NavLink to={baseRoute + "/design"}>Design</NavLink>
          </li>
          <Permission access={[PermissionTypes.RESPONSE_TEMPLATE_READ]}>
            <li>
              <NavLink to={baseRoute + "/templates"}>Data View</NavLink>
            </li>
            <li>
              <NavLink to={baseRoute + "/layouts"}>Layouts</NavLink>
            </li>
          </Permission>
          <Permission access={[PermissionTypes.RULE_READ]}>
            <li>
              <NavLink to={baseRoute + "/workflows"}>Workflows</NavLink>
            </li>
          </Permission>
          <li>
            <NavLink to={baseRoute + "/responses"}>Responses</NavLink>
          </li>
          <Permission access={[PermissionTypes.DASHBOARD_READ]}>
            <li>
              <NavLink to={baseRoute + "/widgets"}>Widgets</NavLink>
            </li>
          </Permission>
        </ul>
        <ul
          className={`top-menu w-320px-important device-view ${showLayoutControls ? "justify-end bg-white" : ""}`}
        >
          {showFormControls && (
            <>
              {/* {isPublishAllowed && ( */}
              <li className="seperator">
                <Button
                  onClick={() => {
                    setSettingsModalOpen(true);
                  }}
                >
                  <CustomTooltip content="Settings">
                    <SettingIcon />
                  </CustomTooltip>
                </Button>
              </li>
              {/* )} */}
              <li className="seperator">
                <Button
                  onClick={() => {
                    setShareModalOpen(true);
                  }}
                >
                  <CustomTooltip content="Share">
                    <ShareIcon />
                  </CustomTooltip>
                </Button>
              </li>
              <li className="seperator">
                <NavLink to={baseRoute + "/preview"}>
                  <CustomTooltip content="Preview">
                    <PlayIcon />
                  </CustomTooltip>
                </NavLink>
              </li>
              <li>
                {isUnPublishAllowed ? (
                  <>
                    <Button
                      loading={loadingFormStatus}
                      onClick={() => triggerFormStatusUpdate(AppAndFormAction.UNPUBLISH)}
                      variant="default-bordered"
                      loaderHeight={24}
                      loaderColor="#00033e"
                      style={{ width: "125px" }}
                    >
                      Unpublish
                    </Button>
                  </>
                ) : null}
                {isPublishAllowed ? (
                  <Button
                    variant="primary"
                    loading={loadingFormStatus}
                    onClick={() => triggerFormStatusUpdate(AppAndFormAction.PUBLISH)}
                    loaderHeight={24}
                    style={{ width: "125px" }}
                  >
                    Publish
                  </Button>
                ) : null}
              </li>
            </>
          )}
          {showLayoutControls && (
            <li>
              <Button
                variant="primary"
                loaderHeight={24}
                onClick={() => saveFn?.()}
                disabled={!saveFn || isSaving}
                loading={isSaving}
                style={{ width: "125px" }}
              >
                Save Layout
              </Button>
            </li>
          )}
        </ul>
        <FormSettingDialog
          isOpen={isSettingsModalOpen}
          onClose={() => {
            setSettingsModalOpen(false);
          }}
        />
        <ShareFormDialog
          appId={appId}
          formId={formId}
          isOpen={isShareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
          }}
        />
      </nav>
      <NetworkStatusBar />
    </>
  );
};
